import type { ReactNode } from "react";
import { ReceiptText } from "lucide-react";
import type { components } from "@dolfin/api-public";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { formatCurrency, formatDate } from "@/lib/format";
import { EmptyState, HeaderRow, LoadingState, TH, TableShell, rowLink } from "@/components/PageUi";
import { cn } from "@/lib/utils";

type CreditNoteResponse = components["schemas"]["CreditNoteResponse"];

// Presentational; shared by the credit-notes list and customer page so their columns can't drift.
export interface CreditNotesTableProps {
  creditNotes: CreditNoteResponse[];
  loading?: boolean;
  emptyMessage?: string;
  /** Row click opens the credit note, or its parent invoice when the host has no credit note page. */
  onRowClick?: (creditNote: CreditNoteResponse) => void;
  /** Supporting line under the empty state's title, e.g. how a credit note gets raised. */
  emptyHint?: ReactNode;
  emptyAction?: ReactNode;
}

// "Issuing" is a brief two-phase claim on the server; to a reader it is still on its way.
export function CreditNoteStatusPill({ status }: { status: string | undefined }) {
  const tone =
    status === "Issued"
      ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
      : status === "Issuing"
        ? "border-accent-blue/30 bg-accent-blue/10 text-accent-blue"
        : "border-border bg-muted text-muted-foreground";
  return (
    <span
      className={cn(
        "inline-flex min-w-[64px] shrink-0 justify-center rounded-md border px-2 py-0.5 text-[11px] font-medium",
        tone,
      )}
    >
      {status ?? "-"}
    </span>
  );
}

function rateLabel(
  snapshot: components["schemas"]["TaxRateSnapshotResponse"] | null | undefined,
): string {
  if (!snapshot) return "";
  const n = Number(snapshot.rate);
  const pct = Number.isFinite(n) ? `${n}%` : "";
  if (snapshot.code) return ` · ${snapshot.code} ${pct}`.trimEnd();
  return pct ? ` · ${pct}` : "";
}

export function CreditNotesTable({
  creditNotes,
  loading,
  emptyMessage = "No credit notes yet.",
  onRowClick,
  emptyHint,
  emptyAction,
}: CreditNotesTableProps) {
  if (loading) {
    return <LoadingState label="Loading credit notes..." />;
  }

  if (creditNotes.length === 0) {
    return (
      <EmptyState icon={ReceiptText} title={emptyMessage} action={emptyAction}>
        {emptyHint ?? "Credit notes are raised from an invoice. Open the invoice and choose New credit note."}
      </EmptyState>
    );
  }

  return (
    <TableShell>
      <Table>
        <TableHeader>
          <HeaderRow>
            <TableHead className={TH}>Credit note</TableHead>
            <TableHead className={cn(TH, "text-right")}>Net</TableHead>
            <TableHead className={cn(TH, "text-right")}>Tax</TableHead>
            <TableHead className={cn(TH, "text-right")}>Amount</TableHead>
            <TableHead className={cn(TH, "w-[100px] text-right")}>Status</TableHead>
            <TableHead className={cn(TH, "text-right")}>Created</TableHead>
          </HeaderRow>
        </TableHeader>
        <TableBody>
          {creditNotes.map((note) => {
            const issued = note.status === "Issued";
            return (
              <TableRow key={note.id} {...rowLink(onRowClick ? () => onRowClick(note) : undefined)}>
                {/* Number and reason together: the reason is what tells two credits on one invoice apart. */}
                <TableCell className="max-w-[280px]">
                  <div className="font-mono text-[13px] font-semibold">{note.documentNumber || "Draft"}</div>
                  <div className="truncate text-xs text-muted-foreground">
                    {note.reason || "No reason given"}
                  </div>
                </TableCell>
                <TableCell className="text-right text-sm tabular-nums text-muted-foreground">
                  {formatCurrency(note.netAmount, note.currency)}
                </TableCell>
                <TableCell className="text-right text-sm tabular-nums text-muted-foreground">
                  {formatCurrency(note.taxAmount, note.currency)}
                  <div className="text-xs text-muted-foreground/70">
                    {rateLabel(note.taxRateSnapshot).replace(/^ · /, "")}
                  </div>
                </TableCell>
                <TableCell
                  className={cn(
                    "text-right font-semibold tabular-nums",
                    issued && "text-emerald-600 dark:text-emerald-400",
                  )}
                >
                  {issued
                    ? `−${formatCurrency(note.amount, note.currency)}`
                    : formatCurrency(note.amount, note.currency)}
                </TableCell>
                <TableCell className="text-right">
                  <CreditNoteStatusPill status={note.status ?? undefined} />
                </TableCell>
                <TableCell className="text-right text-sm text-muted-foreground">
                  {formatDate(note.createdAt)}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableShell>
  );
}
